import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Divider,
  Flex,
  Radio,
  SegmentedControl,
  Text,
  Tooltip,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconList,
  IconPlayerPlay,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { FileTableFilters } from './FileTableFilters';
import { DuplicateFileControls } from './DuplicateFileControls';
import { FileStatistics, FileStatus, MetaValidationProgress, TableMode } from '../types';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/config/routes';

interface FileTableToolbarSlotsProps {
  // Data
  statistics: FileStatistics;
  tableMode: TableMode;
  metaValidationProgress?: MetaValidationProgress;

  // State
  statusFilter: FileStatus | 'all';
  showDuplicatesOf?: string | null;
  duplicateFileNames: string[];
  duplicateFileStats: Record<string, number>;

  // Callbacks
  onStatusFilterChange: (filter: FileStatus | 'all') => void;
  onShowDuplicatesOf?: (fileName: string | null) => void;
  onValidateAll?: () => void;
  onUploadFiles?: () => void;

  // Upload-related
  filesUploaded?: boolean;
  hasValidationResults?: boolean;
  hasDuplicates?: boolean;

  // UI-only flags (used by callers to conditionally render slots)
  canUpload?: boolean;
}

export const useFileTableToolbarSlots = ({
  statistics,
  tableMode,
  statusFilter,
  showDuplicatesOf,
  duplicateFileNames,
  duplicateFileStats,
  metaValidationProgress,
  onStatusFilterChange,
  onShowDuplicatesOf,
  onValidateAll,
  onUploadFiles,
  filesUploaded = false,
  hasValidationResults = false,
  hasDuplicates = false,
}: FileTableToolbarSlotsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasFilesAccess = usePermissionAccess([PERMISSIONS.FILES_ACCESS]);
  // Left slot: Filters and duplicate controls
  const leftSlot = useMemo(
    () => (
      <Flex gap="3" align="center">
        <FileTableFilters
          tableMode={tableMode}
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
        />
        {hasDuplicates && (
          <DuplicateFileControls
            hasDuplicates={hasDuplicates}
            duplicateFileNames={duplicateFileNames}
            duplicateFileStats={duplicateFileStats || {}}
            showDuplicatesOf={showDuplicatesOf}
            onShowDuplicatesOf={onShowDuplicatesOf}
          />
        )}
      </Flex>
    ),
    [
      tableMode,
      statusFilter,
      onStatusFilterChange,
      showDuplicatesOf,
      hasDuplicates,
      duplicateFileNames,
      duplicateFileStats,
      onShowDuplicatesOf,
    ]
  );

  // Center slot: Alert for duplicates
  const centerSlot = useMemo(() => {
    if (!hasDuplicates) return null;

    return (
      <Alert
        icon={<IconAlertTriangle />}
        variant="soft"
        description="Some files have duplicate names and need your attention before proceeding."
        color="error"
        className="bulk-file-table__duplicate-alert"
      />
    );
  }, [hasDuplicates]);

  // Right slot: Statistics with filter buttons and action buttons
  const rightSlot = useMemo(() => {
    const StatButton: React.FC<{
      status: FileStatus | 'all';
      count: number;
      color?: 'default' | 'success' | 'error' | 'warning';
      label: string;
    }> = ({ status, count, color = 'default', label }) => (
      <Button
        variant="outlined"
        color={color}
        size="xsmall"
        onClick={() => onStatusFilterChange(status)}
        startIcon={
          <Radio
            size="1"
            color={
              color === 'success'
                ? 'green'
                : color === 'error'
                  ? 'red'
                  : color === 'warning'
                    ? 'amber'
                    : 'indigo'
            }
            highContrast
            value={status}
            checked={statusFilter === status}
          />
        }
      >
        {label}: {count}
      </Button>
    );

    // Determine current route for SegmentControl
    const currentPath = location.pathname;
    const isUploadPage = currentPath === ROUTES.fileImporter;
    const isFilesPage = currentPath === ROUTES.fileImporterFiles;
    const segmentValue = isFilesPage
      ? ROUTES.fileImporterFiles
      : ROUTES.fileImporter;

    const handleSegmentChange = (value: string) => {
      if (value !== currentPath) {
        navigate(value);
      }
    };

    return (
      <Flex gap="2" direction="row" align="center">
        <StatButton status="all" count={statistics.total} label="Total" />

        {tableMode === TableMode.UPLOAD_PROGRESS ? (
          // Upload progress mode - show upload stats
          <>
            <StatButton
              status="pending"
              count={statistics.pending}
              label="Pending"
            />
            <StatButton
              status="uploading"
              count={statistics.uploading}
              color="warning"
              label="Uploading"
            />
            <StatButton
              status="uploaded"
              count={statistics.uploaded}
              color="success"
              label="Uploaded"
            />
            <StatButton
              status="failed"
              count={statistics.failed}
              color="error"
              label="Failed"
            />
          </>
        ) : !hasValidationResults ? (
          // Before validation - show ready to check and duplicates
          <>
            <StatButton
              status="ready-to-check"
              count={statistics.readyToCheck}
              label="Ready to check"
            />
            <StatButton
              status="duplicated"
              count={statistics.duplicated}
              label="Duplicated"
            />
          </>
        ) : (
          // After validation - show validation results
          <>
            <StatButton
              status="allowed"
              count={statistics.allowed}
              color="success"
              label="Allowed"
            />
            <StatButton
              status="not-allowed"
              count={statistics.notAllowed}
              color="error"
              label="Not Allowed"
            />
            {statistics.exists > 0 && (
              <StatButton
                status="exists"
                count={statistics.exists}
                color="warning"
                label="Exists"
              />
            )}
          </>
        )}

        <Divider orientation="vertical" size="small" />

        {/* Validate All button */}
        {onValidateAll && (
          <Button
            variant="classic"
            color="primary"
            size="small"
            onClick={onValidateAll}
            startIcon={<IconPlayerPlay />}
            disabled={
              hasDuplicates ||
              statistics.serverValidating > 0 ||
              hasValidationResults ||
              Boolean(metaValidationProgress?.isRunning)
            }
            className="validate-all-button"
          >
            Validate All ({statistics?.total})
          </Button>
        )}

        {/* Upload Files button */}
        {onUploadFiles && (
          <Tooltip
            content={
              filesUploaded
                ? 'Files already uploaded'
                : !hasValidationResults
                  ? 'First validate files on server'
                  : statistics.readyToUpload === 0
                    ? 'No files ready for upload'
                    : undefined
            }
          >
            <Button
              variant="classic"
              color="success"
              size="small"
              onClick={onUploadFiles}
              startIcon={<IconPlayerPlay />}
              disabled={
                filesUploaded ||
                !hasValidationResults ||
                statistics.readyToUpload === 0
              }
              className="upload-files-button-with-files-count"
            >
              {filesUploaded
                ? 'Files Uploaded'
                : `Upload Files (${hasValidationResults ? statistics.readyToUpload : 0})`}
            </Button>
          </Tooltip>
        )}

        {/* SegmentControl for page switching */}
        {hasFilesAccess && (isUploadPage || isFilesPage) && (
          <>
            <Divider orientation="vertical" size="small" />
            <SegmentedControl
              items={[
                {
                  value: ROUTES.fileImporter,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <IconUpload size={16} />
                      Upload
                    </span>
                  ),
                },
                {
                  value: ROUTES.fileImporterFiles,
                  label: (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <IconList size={16} />
                      Files
                    </span>
                  ),
                },
              ]}
              value={segmentValue}
              onValueChange={handleSegmentChange}
              size="medium"
              variant="surface"
            />
          </>
        )}
      </Flex>
    );
  }, [
    statistics,
    tableMode,
    hasValidationResults,
    statusFilter,
    onStatusFilterChange,
    onValidateAll,
    onUploadFiles,
    hasDuplicates,
    filesUploaded,
    location.pathname,
    navigate,
    hasFilesAccess,
  ]);

  // Bottom slot: Active filters
  const bottomSlot = useMemo(() => {
    if (!showDuplicatesOf || !onShowDuplicatesOf) return null;

    return (
      <div className="bulk-file-table__active-filters">
        <Flex gap="4" align="center">
          <Text size="2xsmall" color="muted">
            Active Filter:
          </Text>
          <Badge
            variant="outlined"
            color="default"
            size="medium"
            endIcon={
              <Button
                variant="ghost"
                color="error"
                size="xsmall"
                isIconOnly
                onClick={() => onShowDuplicatesOf(null)}
                title="Clear filter"
              >
                <IconX />
              </Button>
            }
          >
            Duplicates of <strong>{showDuplicatesOf}</strong>
          </Badge>
        </Flex>
      </div>
    );
  }, [showDuplicatesOf, onShowDuplicatesOf]);

  return {
    leftSlot,
    centerSlot,
    rightSlot,
    bottomSlot,
  };
};
